import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageLoading } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const SECTIONS = [
  { key: "business_overview", title: "Business Overview", icon: "Building2",
    desc: "What the company does, how it operates, its locations, structure, ownership and current position.",
    fields: [
      ["what_we_do", "What the business does"],
      ["how_it_operates", "How it operates"],
      ["locations", "Locations"],
      ["structure_ownership", "Structure & ownership"],
      ["current_position", "Current position"],
    ] },
  { key: "vision", title: "Vision & Strategic Direction", icon: "Compass",
    desc: "Where the owners want the business to be in one, three and five years — and what success looks like personally and commercially.",
    fields: [
      ["one_year", "One year"],
      ["three_year", "Three years"],
      ["five_year", "Five years"],
      ["personal_success", "Personal success"],
      ["commercial_success", "Commercial success"],
    ] },
  { key: "priorities_challenges", title: "Immediate Priorities & Challenges", icon: "Flag",
    desc: "The main reasons Amden has been engaged, the problems requiring urgent attention and the outcomes expected.",
    fields: [
      ["why_amden", "Why Amden was engaged"],
      ["urgent_problems", "Urgent problems"],
      ["expected_outcomes", "Expected outcomes"],
    ] },
  { key: "financial", title: "Financial Performance", icon: "Euro", financial: true,
    desc: "Turnover, gross margin, profitability, overheads, cash flow, recurring revenue, outstanding debtors and financial targets.",
    fields: [
      ["turnover", "Turnover"],
      ["gross_margin", "Gross margin"],
      ["profitability", "Profitability"],
      ["overheads", "Overheads"],
      ["cash_flow", "Cash flow"],
      ["recurring_revenue", "Recurring revenue"],
      ["debtors", "Outstanding debtors"],
      ["financial_targets", "Financial targets"],
    ] },
  { key: "customers_market", title: "Customers & Market Position", icon: "Users",
    desc: "Ideal customer profile, key accounts, customer concentration, competitive position and what makes the business different.",
    fields: [
      ["ideal_customer", "Ideal customer profile"],
      ["key_accounts", "Key accounts"],
      ["concentration", "Customer concentration"],
      ["competitive_position", "Competitive position"],
      ["differentiation", "Differentiation"],
    ] },
  { key: "sales_marketing", title: "Sales & Marketing", icon: "TrendingUp",
    desc: "How leads are generated, how opportunities are managed, conversion rates, sales targets, marketing activity and pipeline visibility.",
    fields: [
      ["lead_generation", "Lead generation"],
      ["opportunity_management", "Opportunity management"],
      ["conversion_rates", "Conversion rates"],
      ["sales_targets", "Sales targets"],
      ["marketing_activity", "Marketing activity"],
      ["pipeline_visibility", "Pipeline visibility"],
    ] },
  { key: "people", title: "People & Organisational Structure", icon: "UsersRound",
    desc: "Leadership team, reporting lines, responsibilities, staffing challenges, capabilities, performance and company culture.",
    fields: [
      ["leadership_team", "Leadership team"],
      ["reporting_lines", "Reporting lines"],
      ["responsibilities", "Responsibilities"],
      ["staffing_challenges", "Staffing challenges"],
      ["capabilities", "Capabilities & performance"],
      ["culture", "Company culture"],
    ] },
  { key: "operations", title: "Operations & Systems", icon: "Cog",
    desc: "How work moves through the business, current systems, processes, bottlenecks, duplication, manual administration and automation opportunities.",
    fields: [
      ["workflow", "How work flows through the business"],
      ["current_systems", "Current systems"],
      ["processes_bottlenecks", "Processes & bottlenecks"],
      ["manual_admin", "Manual administration"],
      ["automation_opportunities", "Automation opportunities"],
    ] },
  { key: "governance_risk", title: "Governance, Risk & Compliance", icon: "ShieldCheck",
    desc: "Contracts, HR compliance, health & safety, insurance, accreditations, data protection and any significant operational or commercial risks.",
    fields: [
      ["contracts", "Contracts"],
      ["hr_compliance", "HR compliance"],
      ["health_safety", "Health & safety"],
      ["insurance", "Insurance"],
      ["accreditations", "Accreditations"],
      ["data_protection", "Data protection"],
      ["key_risks", "Key risks"],
    ] },
  { key: "objectives_accountability", title: "Objectives, Measures & Accountability", icon: "Target",
    desc: "The agreed objectives for the engagement, KPIs, responsibilities, milestones, communication rhythm and how progress will be reported.",
    fields: [
      ["agreed_objectives", "Agreed objectives"],
      ["kpis", "KPIs"],
      ["responsibilities", "Responsibilities"],
      ["milestones", "Milestones"],
      ["communication_rhythm", "Communication rhythm"],
      ["reporting", "How progress is reported"],
    ] },
];

const KEY_QUESTIONS = [
  ["where_now", "Where is the business now?"],
  ["where_go", "Where does it want to go?"],
  ["whats_preventing", "What is preventing it from getting there?"],
  ["whats_next", "What needs to happen next, who owns it and by when?"],
];

export function CompanyDossier({ orgId }) {
  const { user, isAmden } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("full");
  const [step, setStep] = useState(0);

  const hasFinancial = isAmden || user?.financial_access;
  const canEdit = isAmden || user?.role === "client_admin";

  useEffect(() => {
    if (!orgId) return;
    api.get(`/organisations/${orgId}`).then((r) => { setOrg(r.data); setDossier(r.data.dossier || {}); });
  }, [orgId]);

  const set = (section, field, value) =>
    setDossier((d) => ({ ...d, [section]: { ...(d[section] || {}), [field]: value } }));

  const excluded = useMemo(() => new Set(dossier?.excluded_sections || []), [dossier]);

  const persistDossier = async (next) => {
    try {
      const { data } = await api.patch(`/organisations/${orgId}/dossier`, { dossier: next });
      setDossier(data.dossier);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleExclude = (key) => {
    const cur = new Set(dossier.excluded_sections || []);
    const nowExcluded = !cur.has(key);
    if (nowExcluded) cur.add(key); else cur.delete(key);
    const next = { ...dossier, excluded_sections: Array.from(cur) };
    setDossier(next);
    persistDossier(next);
    toast.success(nowExcluded ? "Section marked not applicable" : "Section marked applicable");
  };

  const completed = useMemo(() => {
    if (!dossier) return 0;
    const secs = SECTIONS.filter((s) => (hasFinancial || !s.financial) && !excluded.has(s.key));
    if (secs.length === 0) return 100;
    const done = secs.filter((s) => {
      const v = dossier[s.key] || {};
      return s.fields.some(([k]) => (v[k] || "").trim());
    }).length;
    return Math.round((done / secs.length) * 100);
  }, [dossier, hasFinancial, excluded]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/organisations/${orgId}/dossier`, { dossier });
      setDossier(data.dossier);
      toast.success("Company dossier saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const exportPdf = () => {
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const secs = SECTIONS.filter((s) => (hasFinancial || !s.financial) && !excluded.has(s.key));
    const kq = dossier.key_questions || {};
    const kqHtml = KEY_QUESTIONS.filter(([k]) => (kq[k] || "").trim())
      .map(([k, label], i) => `<div class="f"><div class="fl">${i + 1}. ${esc(label)}</div><div class="fv">${esc(kq[k])}</div></div>`).join("");
    const secHtml = secs.map((s) => {
      const v = dossier[s.key] || {};
      const rows = s.fields.filter(([k]) => (v[k] || "").trim())
        .map(([k, label]) => `<div class="f"><div class="fl">${esc(label)}</div><div class="fv">${esc(v[k])}</div></div>`).join("");
      return rows ? `<section><h2>${esc(s.title)}</h2>${rows}</section>` : "";
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Company Dossier — ${esc(org?.company_name || "")}</title>
      <style>
        *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#1E2D44;margin:0;padding:48px 56px;line-height:1.55}
        .hd{border-bottom:3px solid #FFB000;padding-bottom:16px;margin-bottom:28px}
        .ey{color:#B87F1E;font-size:11px;letter-spacing:.14em;text-transform:uppercase;font-family:Arial,sans-serif;font-weight:700}
        h1{font-family:Arial,sans-serif;color:#0B1729;font-size:26px;margin:6px 0 2px}
        .sub{color:#5b6a82;font-size:13px}
        h2{font-family:Arial,sans-serif;color:#0B1729;font-size:15px;background:#F4F6F9;border-left:3px solid #FFB000;padding:8px 12px;margin:26px 0 12px}
        .f{margin:0 0 12px} .fl{font-family:Arial,sans-serif;font-weight:700;color:#0B1729;font-size:12px;margin-bottom:2px}
        .fv{font-size:13px;white-space:pre-wrap} section{break-inside:avoid}
        @media print{body{padding:24px}}
      </style></head><body>
      <div class="hd"><div class="ey">Amden Compass · Company Dossier</div>
      <h1>${esc(org?.company_name || "Company Dossier")}</h1>
      <div class="sub">Onboarding intake &amp; discovery · Generated ${new Date().toLocaleDateString("en-GB")}</div></div>
      ${kqHtml ? `<section><h2>Four key questions</h2>${kqHtml}</section>` : ""}
      ${secHtml || "<p>No dossier information has been captured yet.</p>"}
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to export the dossier PDF."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 350);
  };

  if (!dossier) return <PageLoading />;

  const visibleSections = SECTIONS.filter((s) => hasFinancial || !s.financial);
  const steps = [
    { key: "key_questions", title: "Four key questions", icon: "HelpCircle", desc: "A strong onboarding answers these clearly.", fields: KEY_QUESTIONS },
    ...visibleSections.filter((s) => !excluded.has(s.key)).map((s) => ({ key: s.key, title: s.title, icon: s.icon, desc: s.desc, fields: s.fields })),
  ];

  if (mode === "wizard") {
    const cur = steps[step];
    const WIcon = Icons[cur.icon] || Icons.Circle;
    const isLast = step === steps.length - 1;
    return (
      <div className="space-y-5" data-testid="company-dossier">
        <div className="amden-card overflow-hidden p-0" data-testid="dossier-wizard">
          <div className="border-b border-[#E4E8EF] bg-[#0B1729] px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#FFC24D]"><Icons.Wand2 size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Guided intake</span></div>
              <button onClick={() => setMode("full")} className="text-xs text-white/60 hover:text-white" data-testid="wizard-exit">Exit guided mode</button>
            </div>
            <h3 className="mt-1 font-display text-lg font-bold text-white">Step {step + 1} of {steps.length} — {cur.title}</h3>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-[#FFB000] transition-all" style={{ width: `${Math.round(((step + 1) / steps.length) * 100)}%` }} /></div>
          </div>
        </div>

        <div className="amden-card p-6" data-testid={`wizard-step-${cur.key}`}>
          <div className="mb-4 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B1729] text-[#FFC24D]"><WIcon size={17} /></span>
            <div><h3 className="font-display text-base font-bold text-[#0B1729]">{cur.title}</h3><p className="text-xs text-[#1E2D44]/55">{cur.desc}</p></div>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {cur.fields.map(([k, label]) => (
              <div key={k}>
                <Label className="text-sm">{label}</Label>
                <Textarea value={(dossier[cur.key] || {})[k] || ""} onChange={(e) => set(cur.key, k, e.target.value)} disabled={!canEdit} rows={2} className="mt-1.5" data-testid={`dossier-${cur.key}-${k}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wizard-back"><Icons.ChevronLeft size={16} className="mr-1" /> Back</Button>
          <div className="flex items-center gap-2">
            {canEdit && <Button variant="outline" onClick={save} disabled={saving} data-testid="wizard-save">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save progress"}</Button>}
            {isLast ? (
              canEdit && <Button onClick={async () => { await save(); setMode("full"); }} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="wizard-finish"><Icons.Check size={16} className="mr-1" /> Save &amp; finish</Button>
            ) : (
              <Button onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="wizard-next">Next <Icons.ChevronRight size={16} className="ml-1" /></Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="company-dossier">
      {/* Intro + progress */}
      <div className="amden-card overflow-hidden p-0">
        <div className="flex flex-col gap-3 border-b border-[#E4E8EF] bg-[#0B1729] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-[#FFC24D]"><Icons.ClipboardList size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Onboarding dossier</span></div>
            <h3 className="mt-1 font-display text-lg font-bold text-white">Company Intake & Discovery</h3>
            <p className="mt-0.5 text-sm text-white/55">A complete picture of the business for the Lead Consultant.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="text-right"><div className="font-display text-2xl font-bold text-white">{completed}%</div><div className="text-[11px] text-white/45">complete</div></div>
            <Button onClick={exportPdf} variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="export-dossier-btn">
              <Icons.FileDown size={16} className="mr-1.5" /> Export PDF
            </Button>
            {canEdit && (
              <Button onClick={() => { setStep(0); setMode("wizard"); }} variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="start-wizard-btn">
                <Icons.Wand2 size={16} className="mr-1.5" /> Guided intake
              </Button>
            )}
            {canEdit && (
              <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-dossier-btn">
                {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : <><Icons.Save size={16} className="mr-1.5" /> Save</>}
              </Button>
            )}
          </div>
        </div>
        {!canEdit && <div className="px-6 py-3 text-sm text-[#1E2D44]/60">You have view-only access to the dossier.</div>}
      </div>

      {/* Four key questions */}
      <div className="amden-card p-6">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFB000]/15 text-[#B87F1E]"><Icons.HelpCircle size={16} /></span>
          <div><h3 className="font-display text-base font-bold text-[#0B1729]">Four key questions</h3><p className="text-xs text-[#1E2D44]/55">A strong onboarding answers these clearly.</p></div>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {KEY_QUESTIONS.map(([k, label], i) => (
            <div key={k}>
              <Label className="text-sm font-semibold text-[#0B1729]">{i + 1}. {label}</Label>
              <Textarea value={(dossier.key_questions || {})[k] || ""} onChange={(e) => set("key_questions", k, e.target.value)} disabled={!canEdit} rows={2} className="mt-1.5" data-testid={`kq-${k}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Sections */}
      <Accordion type="multiple" defaultValue={["business_overview"]} className="space-y-3">
        {visibleSections.map((s) => {
          const Icon = Icons[s.icon] || Icons.Circle;
          const v = dossier[s.key] || {};
          const filled = s.fields.filter(([k]) => (v[k] || "").trim()).length;
          const isExcluded = excluded.has(s.key);
          return (
            <AccordionItem key={s.key} value={s.key} className={`amden-card border-0 px-5 ${isExcluded ? "opacity-60" : ""}`} data-testid={`dossier-section-${s.key}`}>
              <AccordionTrigger className="py-4 hover:no-underline">
                <div className="flex items-center gap-3 text-left">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0B1729] text-[#FFC24D]"><Icon size={17} /></span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-display text-base font-bold text-[#0B1729]">{s.title}</span>
                      {s.financial && <span className="rounded-full bg-[#FFB000]/15 px-2 py-0.5 text-[10px] font-semibold text-[#B87F1E]">Financial</span>}
                      {isExcluded && <span className="rounded-full bg-[#1E2D44]/8 px-2 py-0.5 text-[10px] font-semibold text-[#1E2D44]/55" data-testid={`dossier-na-badge-${s.key}`}>Not applicable</span>}
                    </div>
                    <div className="text-xs font-normal text-[#1E2D44]/55">{isExcluded ? "Excluded from onboarding progress" : `${filled}/${s.fields.length} completed`}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                {isAmden && (
                  <div className="mb-4 flex items-center justify-between rounded-lg border border-[#E4E8EF] bg-[#F7F9FC] px-3.5 py-2.5">
                    <div>
                      <div className="text-sm font-medium text-[#0B1729]">Applicable to this client</div>
                      <div className="text-[11px] text-[#1E2D44]/55">Turn off if this section isn't relevant — it won't count towards onboarding progress.</div>
                    </div>
                    <Switch checked={!isExcluded} onCheckedChange={() => toggleExclude(s.key)} data-testid={`dossier-toggle-${s.key}`} />
                  </div>
                )}
                <p className="mb-4 text-sm text-[#1E2D44]/60">{s.desc}</p>
                <div className={`grid grid-cols-1 gap-4 lg:grid-cols-2 ${isExcluded ? "pointer-events-none opacity-50" : ""}`}>
                  {s.fields.map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-sm">{label}</Label>
                      <Textarea value={v[k] || ""} onChange={(e) => set(s.key, k, e.target.value)} disabled={!canEdit || isExcluded} rows={2} className="mt-1.5" data-testid={`dossier-${s.key}-${k}`} />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-dossier-btn-bottom">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : <><Icons.Save size={16} className="mr-1.5" /> Save dossier</>}
          </Button>
        </div>
      )}
    </div>
  );
}
